import * as React from 'react';
import { Button, Callout, Text } from '../../ui/index.js';
import { Focusable, FocusZone } from '../focus/Focusable.js';
import { SNOOZE_MS, installUpdate, useAppUpdate } from '../../lib/appUpdate.js';

/**
 * The one interruption an app update is allowed (`04-launcher-and-patcher.md` section 1.2).
 *
 * It appears only once the download has finished, so the restart it offers is immediate. A
 * download in progress says nothing: nobody can act on it, and a launcher narrating its own
 * housekeeping is noise on a screen that is meant to be about games.
 *
 * "Later" is a snooze rather than a silence. Nothing installs unless this banner is used — a
 * download that is never restarted into is discarded when the app closes and fetched again next
 * launch — so an app left open for days after one "Later" would be an app that keeps checking
 * for updates it never mentions. It comes back on the same rhythm the check runs on, and a
 * version newer than the one that was waved off comes back immediately.
 */
export function UpdateBanner() {
  const update = useAppUpdate();
  const [dismissed, setDismissed] = React.useState<string | null>(null);

  // Long enough not to nag, short enough that a machine that stays on all week is still told.
  React.useEffect(() => {
    if (dismissed === null) return;
    const timer = setTimeout(() => setDismissed(null), SNOOZE_MS);
    return () => clearTimeout(timer);
  }, [dismissed]);

  const installing = update.status === 'installing';
  if (update.status !== 'ready' && !installing) return null;
  if (update.version && update.version === dismissed) return null;

  return (
    <FocusZone focusKey="update-banner" direction="row" className="gb-update-banner">
      <Callout.Root className="gb-update-banner__card">
        <div className="gb-row">
          <Text size="2">
            {`Version ${update.version} is downloaded. Restarting takes a moment.`}
          </Text>
          <span className="gb-seat__spacer" />

          <Focusable
            onSelect={() => void installUpdate()}
            selectLabel="Restart now"
            focusKey="update-banner-restart"
            disabled={installing}
            className="gb-focusable--flat"
          >
            <Button size="2" disabled={installing}>
              {installing ? 'Restarting' : 'Restart now'}
            </Button>
          </Focusable>

          <Focusable
            onSelect={() => setDismissed(update.version)}
            selectLabel="Later"
            focusKey="update-banner-later"
            disabled={installing}
            className="gb-focusable--flat"
          >
            <Button size="2" variant="ghost" disabled={installing}>
              {'Later'}
            </Button>
          </Focusable>
        </div>
      </Callout.Root>
    </FocusZone>
  );
}
